import styled from "styled-components";
import { Flex, Text, Image } from "rebass/styled-components";
import { useHistory } from "react-router-dom";
import { FaStar } from "react-icons/fa";
import { BsArrowRight } from "react-icons/bs";

import { ITeam } from "MVVM/@types";
import { TeamBadge } from "MVVM/Components/Teams/TeamBadge";
import { GamePills } from "../GamePills";

interface ITeamCardProps {
  team: ITeam;
}

export const TeamCard = ({ team }: ITeamCardProps) => {
  const history = useHistory();
  const fallbackCoverUrl = `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/profile/banner/allstar.jpg`;
  const coverUrl = team.coverUrl ?? fallbackCoverUrl;
  const role = team.userRole || "MEMBER";
  const isOwner = role === "OWNER";
  const memberCount = team.memberCount ?? team.members?.length ?? 0;

  return (
    <Card onClick={() => history.push(`/team/${team.slug}`)}>
      <Banner bannerUrl={coverUrl}>
        <RoleBadge $isOwner={isOwner}>
          {isOwner && <FaStar size={10} />}
          {isOwner ? "Owner" : "Member"}
        </RoleBadge>
      </Banner>

      <Head>
        <Avatar src={team.avatarUrl} alt={team.name} />
        <HeadText>
          <NameRow>
            <TeamName>{team.name}</TeamName>
            {team.tag && (
              <TeamBadge team={team} size="small" isClickable={false} />
            )}
          </NameRow>
          <MemberCount>
            {memberCount} {memberCount === 1 ? "Member" : "Members"}
          </MemberCount>
        </HeadText>
      </Head>

      <Body>
        <Bio>{team.bio || "This team hasn't added a bio yet."}</Bio>
        <GamePillsRow>
          <GamePills playedGames={team.playedGames} />
        </GamePillsRow>
      </Body>

      <Footer>
        <FooterMeta>
          {memberCount} {memberCount === 1 ? "Member" : "Members"}
        </FooterMeta>
        <OpenButton
          onClick={(e) => {
            e.stopPropagation();
            history.push(`/team/${team.slug}`);
          }}
        >
          Open Team <BsArrowRight size={14} />
        </OpenButton>
      </Footer>
    </Card>
  );
};

const Card = styled(Flex)`
  background: ${({ theme }) => theme.colors.navy300};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 12px;
  cursor: pointer;
  flex-direction: column;
  overflow: hidden;
  transition: border-color 0.2s ease;
  width: 100%;

  &:hover {
    border-color: ${({ theme }) => theme.colors.navy};
  }
`;

const Banner = styled(Flex)<{ bannerUrl: string }>`
  background:
    linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.5) 0%,
      rgba(0, 0, 0, 0.3) 50%,
      ${({ theme }) => theme.colors.navy300} 100%
    ),
    url(${({ bannerUrl }) => bannerUrl}) center / cover no-repeat;
  height: 130px;
  position: relative;
  width: 100%;
`;

const RoleBadge = styled(Flex)<{ $isOwner: boolean }>`
  align-items: center;
  background: ${({ $isOwner }) =>
    $isOwner ? "rgba(77, 189, 148, 0.9)" : "rgba(47, 70, 86, 0.9)"};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow, sans-serif;
  font-size: 12px;
  font-weight: 700;
  gap: 5px;
  letter-spacing: 0.5px;
  padding: 5px 10px;
  position: absolute;
  right: 12px;
  text-transform: uppercase;
  top: 12px;
`;

const Head = styled(Flex)`
  align-items: center;
  gap: 14px;
  margin-top: -28px;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const Avatar = styled(Image)`
  background: ${({ theme }) => theme.colors.navy};
  border: 3px solid ${({ theme }) => theme.colors.navy300};
  border-radius: 12px;
  flex-shrink: 0;
  height: 64px;
  object-fit: cover;
  width: 64px;
`;

const HeadText = styled(Flex)`
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const NameRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const TeamName = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MemberCount = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 15px;
  font-weight: 500;
`;

const Body = styled(Flex)`
  flex: 1;
  flex-direction: column;
  gap: 16px;
  padding: 16px 20px 0;
`;

const Bio = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  display: -webkit-box;
  font-size: 15px;
  line-height: 22px;
  max-height: 44px;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const GamePillsRow = styled(Flex)`
  flex-wrap: wrap;
`;

const Footer = styled(Flex)`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.navy50};
  justify-content: space-between;
  margin-top: 16px;
  padding: 14px 20px;
`;

const FooterMeta = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 500;
`;

const OpenButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  display: flex;
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 0.3px;
  padding: 0;
  text-transform: uppercase;

  &:hover {
    opacity: 0.8;
  }
`;
